import {
  createInitializeMint2Instruction,
  getMinimumBalanceForRentExemptMint,
  MINT_SIZE,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { Connection, PublicKey, Signer, SystemProgram } from "@solana/web3.js";
import { findDSTInfoAddress } from "./utils.js";
import { makeDSTProgram } from "./makeDSTProgram.js";
import { MethodsBuilder } from "@coral-xyz/anchor/dist/cjs/program/namespace/methods.js";
import { DirectedStakeToken } from "./idlTypes.js";
import { Provider } from "@coral-xyz/anchor";

/**
 * Creating a DST is as follows:
 *
 * 1. Create a Mint
 * 2. Set the Mint metadata and Metaplex update authority
 * 3. Transfer mint authority to DST PDA
 * 4. Create the DST
 * 5. Initialize the DST director
 * 6. Set the DST `stake_target`
 *
 * @param provider
 */
export const createDST = async ({
  connection,
  payer,
  mintKP,
  operator = payer,
  partner = operator,
  baseFee = 100,
  operatorFee = 5_000,
  provider,
}: {
  connection: Connection;
  payer: PublicKey;
  mintKP: Signer;
  operator?: PublicKey;
  partner?: PublicKey;
  baseFee?: number;
  operatorFee?: number;
  provider?: Provider;
}): Promise<{
  result: MethodsBuilder<
    DirectedStakeToken,
    DirectedStakeToken["instructions"][number] & { name: "createDst" }
  >;
  dstAddress: PublicKey;
}> => {
  const lamports = await getMinimumBalanceForRentExemptMint(connection);
  const program = makeDSTProgram(provider);
  const dst = findDSTInfoAddress(mintKP.publicKey);
  const result = program.methods
    .createDst(baseFee, operatorFee)
    .accounts({
      payer,
      tokenMint: mintKP.publicKey,
      operator,
      partner,
    })
    .preInstructions(
      [
        SystemProgram.createAccount({
          fromPubkey: payer,
          newAccountPubkey: mintKP.publicKey,
          space: MINT_SIZE,
          lamports,
          programId: TOKEN_PROGRAM_ID,
        }),
        createInitializeMint2Instruction(
          mintKP.publicKey,
          9,
          dst,
          null,
          TOKEN_PROGRAM_ID,
        ),
      ],
      true,
    );

  return { result, dstAddress: dst };
};
